import { ChangeDetectorRef, Component, Input, OnInit, signal } from '@angular/core';
import { scrollToTop } from '@scripts/utils/dom';
import { ModalContent } from '@src/shared/modal/confirm-modal/confirm-modal.type';
import { ModalService } from '@src/shared/modal/modal.service';
import { trackById } from '../../../utils/object';
import { UserRole } from '../../../utils/user-roles';
import {
  SystemMessage,
  SystemMessageParams,
  SystemMessageService,
} from '../../../woo_services.module/SystemMessageService';
import { TranslationService } from '../../../woo_services.module/TranslationService';
import { wooId } from '../../../woo_services.module/shared-types';

@Component({
  selector: 'system-messages',
  templateUrl: './system-messages.component.html',
  styles: [
    `
      .large-bottom-block {
        overflow-anchor: none;
      }
    `,
  ],
  standalone: false,
})
export class SystemMessages implements OnInit {
  newMessage: SystemMessageParams = {
    message: '',
    type: 'info',
    roles: [],
  };
  show = {
    createMessageForm: false,
  };
  roles: Role[];

  systemMessages = signal<SystemMessage[]>([]);
  loading = signal<boolean>(false);

  @Input('headerPos') parentHeader: HTMLElement;
  constructor(
    private modalService: ModalService,
    private systemMessageService: SystemMessageService,
    private translationService: TranslationService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.roles = this.initRoleList();
    this.loading.set(true);
    this.systemMessageService
      .getAll()
      .then((messages) => {
        this.systemMessages.set(messages);
      })
      .finally(() => {
        this.loading.set(false);
        this.changeDetectorRef.detectChanges();
      });
  }

  translatedRoles(roles: UserRole[]): string {
    return roles.map((role: UserRole) => this.translationService.convertRoles(role)).join(', ');
  }

  addMessage(message: SystemMessageParams): void {
    message.message = message.message.trim();
    if (this.roles.filter((role: Role) => !role.selected).length !== 0) {
      message.roles = this.roles
        .filter((role: Role) => role.selected)
        .reduce<UserRole[]>((userRoles: UserRole[], role: Role) => {
          userRoles.push(role.userRole);
          return userRoles;
        }, []);
    }
    this.loading.set(true);
    this.systemMessageService
      .create(message)
      .then((newMessage) => {
        this.systemMessages.update((messages) => [...messages, newMessage]);
        this.newMessage = {
          message: '',
          type: 'info',
          roles: [],
        };
        this.selectAllRoles();
        this.show.createMessageForm = false;
        this.modalService.openSuccess('Meddelandet är nu skapat!');
      })
      .finally(() => {
        this.loading.set(false);
      });
  }

  removeMessage(message: SystemMessage): void {
    const dialogContent: ModalContent = {
      title: 'Ta bort meddelande',
      texts: ['Är du säker på att du vill ta bort följande meddelande?', message.message],
      confirmLabel: 'Ja, ta bort',
      cancelLabel: 'Avbryt',
    };

    this.modalService.openConfirm(dialogContent).then((result) => {
      if (result.confirm) {
        this.loading.set(true);
        this.systemMessageService
          .remove(message.id)
          .then(() => {
            this.modalService.openSuccess('Meddelandet är nu borttaget.');
            this.systemMessages.update((messages) => messages.filter((m) => m.id !== message.id));
            this.changeDetectorRef.detectChanges();
          })
          .catch((err) => {
            this.modalService.openError(
              'Ett fel uppstod när meddelandet togs bort.',
              this.translationService.convertError(err),
            );
          })
          .finally(() => {
            this.loading.set(false);
            this.changeDetectorRef.detectChanges();
          });
      }
    });
  }

  disableSubmit(message: SystemMessageParams): boolean {
    return message.message.trim().length === 0 || this.roles.filter((role: Role) => role.selected).length === 0;
  }

  cancelNewMessage(): void {
    this.newMessage = {
      message: '',
      type: 'info',
      roles: [],
    };
    this.show.createMessageForm = false;
    scrollToTop(true);
    this.selectAllRoles();
  }

  trackById(): (_: any, item: { id: wooId }) => wooId {
    return trackById;
  }

  trackRole(_: any, role: { userRole: UserRole }): UserRole {
    return role.userRole;
  }

  bookingRoles(): Role[] {
    return this.roles.filter((role: Role) => role.bookingRole);
  }

  noneBookingRoles(): Role[] {
    return this.roles.filter((role: Role) => !role.bookingRole);
  }

  selectAllRoles(): void {
    this.selectionAllRows(true);
  }

  deselectAllRoles(): void {
    this.selectionAllRows(false);
  }

  roleName(userRole: UserRole): string {
    return this.translationService.convertRoles(userRole);
  }

  showNewSystemMessage(value: boolean): void {
    this.show.createMessageForm = true;

    if (!value) {
      scrollToTop(true);
    }
  }

  private selectionAllRows(selected: boolean): void {
    this.roles.map((role: Role) => {
      role.selected = selected;
    });
  }

  private initRoleList(): Role[] {
    return [
      this.createRole(UserRole.admin, true),
      this.createRole(UserRole.planner, true),
      this.createRole(UserRole.agencyAdmin, true),
      this.createRole(UserRole.agencyUser, true),
      this.createRole(UserRole.client, true),
      this.createRole(UserRole.externalPlanner, true),
      this.createRole(UserRole.accounting, false),
      this.createRole(UserRole.eos, false),
      this.createRole(UserRole.sales, false),
      this.createRole(UserRole.salesManager, false),
    ];
  }

  private createRole(userRole: UserRole, bookingRole: boolean): Role {
    return {
      label: this.roleName(userRole),
      selected: true,
      userRole: userRole,
      bookingRole: bookingRole,
    };
  }
}

interface Role {
  label: string;
  selected: boolean;
  userRole: UserRole;
  bookingRole: boolean;
}
